import { PracticeInputData } from "../../@lib/types";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Stepper, { Step } from "../../components/@common/Stepper";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import PracticeInfoForm from "./PracticeInfoForm";
import PoliciesInfoForm from "./PoliciesInfoForm";
import PeopleInfoForm from "./PeopleInfoForm";
import SvgIcon from "@mui/material/SvgIcon";
import { ReactComponent as PolicyIcon } from "../../@lib/icons/policy-icon.svg";
import { ReactComponent as PracticesIcon } from "../../@lib/icons/practices-icon.svg";
import { ReactComponent as PersonIcon } from "../../@lib/icons/person-icon.svg";
import { FormProvider } from "react-hook-form";

import { useState, useCallback, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { gql } from "@apollo/client";
import { useApolloQuery, usePeoplesCall } from "../../hooks";
import { useAuthSession } from "../../components/AuthSessionProvider";
import { Box, Modal } from "@mui/material";
import { ModalCloseIcon } from "../../@lib/icons";
import { ConditionalRenderer, useLanguageTranslator } from "../../components";
import { moduleKeys } from "../../utils/constants";
import { PersonNew, PolicyNew } from "../../pages";

export type PracticeEditorProps = {
  initialValues?: PracticeInputData;
  disabled?: boolean;
  onCancel?: () => void;
  onFinish?: (practiceData: PracticeInputData) => void;
  isPolicyCreateModified: boolean;
  imageAttachmentDetails?: any;
  documentAttachmentDetails?: any;
  setImageAttachmentDetails?: any;
  setDocumentAttachmentDetails?: any;
  setSelectedListPolicies?: any;
  selectedListPolicies?: any;
  setSelectedListPeople?: any;
  selectedListPeople?: any;
  practiceData?: any;
};

const practiceFormOptionsQueryDocument = gql`
  query PracticeFormOptionsQuery {
    categoryOptions: practiceCategories {
      value: id
      label: name
      practiceTypeOptions: practiceTypes {
        value: id
        label: name
      }
    }
    countryOptions: countries {
      value: id
      label: name
    }
    themeOptions: themes {
      value: id
      label: title
    }
    policyOptions: policies {
      collection {
        value: id
        label: title
      }
    }
  }
`;

const style = {
  position: "absolute",
  margin: "2% 0px",
  width: "98%",
  left: "1%",
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  overflow: "auto",
  height: "90dvh",
};

const PracticeEditor = (props: any) => {
  const [open, setOpen] = useState({ moduleKey: "", status: false });
  const [currStepIndex, setCurrStepIndex] = useState(0);

  const {
    initialValues,
    disabled,
    onCancel = () => void 0,
    onFinish = () => void 0,
    isNotPolicyCreateModified,
    imageAttachmentDetails,
    documentAttachmentDetails,
    setImageAttachmentDetails,
    setDocumentAttachmentDetails,
    setSelectedListPolicies,
    selectedListPolicies,
    setSelectedListPeople,
    selectedListPeople,
    practiceData,
  } = props;

  const { user } = useAuthSession();
  const { translator } = useLanguageTranslator();

  const STEPS: Step[] = [
    {
      label: translator("Practice Information"),
      icon: <SvgIcon component={PracticesIcon} inheritViewBox />,
    },
    {
      label: translator("Policies"),
      icon: <SvgIcon component={PolicyIcon} inheritViewBox />,
    },
    {
      label: translator("People"),
      icon: <SvgIcon component={PersonIcon} inheritViewBox />,
    },
  ];

  const { data, loading, refetch } = useApolloQuery({
    queryDocument: practiceFormOptionsQueryDocument,
  });

  const { data: peopleData } = usePeoplesCall({ isPrivate: true, user: user });

  const modifiedPeopleListData = peopleData?.people?.collection?.map(
    (item: any) => {
      const copyItem = { ...(item || {}) };
      return {
        label: `${copyItem?.firstname} ${copyItem?.lastname}` || "",
        value: copyItem?.id,
      };
    }
  );

  const totalSteps = !isNotPolicyCreateModified
    ? STEPS?.slice(0, 1).length
    : STEPS.length;

  const form = useForm<PracticeInputData>({
    defaultValues: {
      themeId: "",
      countryId: "",
      practiceCategoryId: "",
      practiceTypeId: "",
      // policyId: "",
    },
    values: initialValues,
  });

  const practiceCategoryIdWatch = form.watch("practiceCategoryId");

  const practiceTypeOptions = useMemo(() => {
    const practiceCategoryArray = data?.categoryOptions?.find(
      (op: any) => op.value === form?.getValues()?.practiceCategoryId
    );
    return practiceCategoryArray?.practiceTypeOptions || [];
  }, [practiceCategoryIdWatch, data?.categoryOptions]);

  /**
   * handleContinue()
   */
  const handleContinue = useCallback(async () => {
    const isFormValid = await (() => {
      switch (currStepIndex) {
        case 0:
        case 1:
        case 2:
        default:
          return form.trigger();
      }
    })();

    if (isFormValid) setCurrStepIndex((currVal) => ++currVal);
    else void 0; // TODO: Show notification.
  }, [currStepIndex, form]);

  /**
   * handleBack()
   */
  const handleBack = useCallback(
    () => setCurrStepIndex((currVal) => --currVal),
    []
  );

  function handleClose() {
    setOpen({ moduleKey: "", status: false });
  }

  function onClickHandler(module: string) {
    setOpen({ moduleKey: module, status: true });
  }

  useEffect(() => {
    if (currStepIndex || currStepIndex === 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currStepIndex]);

  return (
    <div>
      <Container maxWidth="sm">
        <Stepper
          steps={!isNotPolicyCreateModified ? STEPS?.slice(0, 1) : STEPS}
          activeStep={currStepIndex}
        />
      </Container>

      <Divider sx={{ m: 4, opacity: 0.5 }} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onFinish)}>
          {currStepIndex === 0 && (
            <PracticeInfoForm
              countryOptions={data?.countryOptions || []}
              themeOptions={data?.themeOptions || []}
              categoryOptions={data?.categoryOptions || []}
              practiceTypeOptions={practiceTypeOptions}
              disabled={loading || disabled}
              imageAttachmentDetails={imageAttachmentDetails}
              setImageAttachmentDetails={setImageAttachmentDetails}
              documentAttachmentDetails={documentAttachmentDetails}
              setDocumentAttachmentDetails={setDocumentAttachmentDetails}
              data={practiceData}
            />
          )}
          {currStepIndex === 1 && (
            <PoliciesInfoForm
              policyOptions={data?.policyOptions?.collection || []}
              disabled={loading || disabled}
              setSelectedListPolicies={setSelectedListPolicies}
              selectedListPolicies={selectedListPolicies}
              onClickHandler={() => onClickHandler(moduleKeys.policy)}
            />
          )}
          {currStepIndex === 2 && (
            <PeopleInfoForm
              data={modifiedPeopleListData || []}
              disabled={loading || disabled}
              state={selectedListPeople}
              setState={setSelectedListPeople}
              onClickHandler={() => onClickHandler(moduleKeys.people)}
            />
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
            {currStepIndex === 0 && (
              <Button
                variant="contained"
                color="inherit"
                onClick={onCancel}
                disabled={loading || disabled}
                sx={{ px: 5 }}
              >
                {translator("Cancel")}
              </Button>
            )}
            {currStepIndex > 0 && currStepIndex < totalSteps && (
              <Button
                variant="contained"
                color="inherit"
                onClick={handleBack}
                disabled={loading || disabled}
                sx={{ px: 5 }}
              >
                {translator("support.Back")}
              </Button>
            )}
            {currStepIndex < totalSteps - 1 && (
              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={disabled}
                sx={{ px: 5 }}
              >
                {translator("generic.Continue")}
              </Button>
            )}
            {currStepIndex === totalSteps - 1 && (
              <Button
                type="submit"
                variant="contained"
                disabled={loading || disabled}
                sx={{ px: 5 }}
              >
                {translator("generic.Finish")}
              </Button>
            )}
          </Stack>
        </form>
      </FormProvider>
      <Modal
        open={open?.status}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Stack
            sx={{
              display: "flex",
              mt: 2,
              justifyContent: "end",
              alignItems: "end",
            }}
          >
            <Button
              onClick={handleClose}
              sx={{ padding: "0px", minWidth: "auto" }}
            >
              <ModalCloseIcon />
            </Button>
          </Stack>
          <ConditionalRenderer
            condition={open?.status && open?.moduleKey === moduleKeys.policy}
          >
            <PolicyNew
              isNotPracticeCreateModule={false}
              refetchFormOptions={refetch}
              onClosePopup={handleClose}
            />
          </ConditionalRenderer>
          <ConditionalRenderer
            condition={open?.status && open?.moduleKey === moduleKeys.people}
          >
            <PersonNew
              isNotPeopleCreateModified={false}
              onClosePopup={handleClose}
            />
          </ConditionalRenderer>
        </Box>
      </Modal>
    </div>
  );
};

export { PracticeEditor };
